/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Form, Input, InputGroup, Label } from 'reactstrap';
import G17Client from '../../../../services/G17Client';
import { nl2br } from '../../../../utils';
import { CollapseButton, CollapseContent, CollapsePanel } from '../../../../components/collapse-panel';
import { AIResponse } from '../../../../types/ai';
import { AILoadingPlaceholder } from './AILoadingPlaceholder';
import { useTypewriter } from '../../hooks/useTypewriter';
import { ButtonGradient } from '../ButtonGradient/ButtonGradient';

interface AIQuestionAndAnswerProps {
  initiativeId: string;
  surveyId: string;
  defaultQuestion?: string;
}

export const AIQuestionAndAnswer = ({ initiativeId, surveyId, defaultQuestion }: AIQuestionAndAnswerProps) => {

  const [isLoading, setLoading] = useState(false);
  const [question, setQuestion] = useState<string | undefined>(defaultQuestion);
  const [aiResponse, setAIResponse] = useState<AIResponse | undefined>();

  const typewriter = useTypewriter({
    initialStr: '',
    appendStr: aiResponse?.content ?? ''
  });

  const isWriting = typewriter.isWriting;
  const response = isWriting ? typewriter.liveResult : typewriter.finalResult;

  const getAnswer = () => {
    if (isLoading || !question) {
      return;
    }
    setLoading(true);
    G17Client.getSurveyQuestionAndAnswer(initiativeId, surveyId, question)
      .then(setAIResponse)
      .finally(() => setLoading(false));
  }

  if (!defaultQuestion) {
    const showAnswerBlock = isLoading || isWriting || response;
    return (
      <Form onSubmit={e => { e.preventDefault(); getAnswer() }}>
        <div>
          <InputGroup>
            <Input placeholder={'What would you like to know?'} onChange={e => setQuestion(e.target.value)} />
            <ButtonGradient onClick={getAnswer}>
              <i className='fal fa-sparkles mr-2' />Ask
            </ButtonGradient>
          </InputGroup>
        </div>
        {showAnswerBlock ?
          <div className='mt-2 ps-2 pe-6'>
            <Label>Answer:</Label>
            <AILoadingPlaceholder isLoading={false} isWriting={isLoading || isWriting}>
              <div className='background-ThemeBgLight border-radius w-100 py-3 px-5'>
                {isLoading
                  ? <span className='text-gradient'>Thinking...</span>
                  : nl2br(response ?? '')
                }
              </div>
            </AILoadingPlaceholder>
          </div>
          : null}
        <hr />
      </Form>
    );
  }

  return (
    <CollapsePanel className='mb-2' collapsed={true} onCollapse={(collapsed) => collapsed ? null : getAnswer()}>
      <CollapseButton onClick={getAnswer}>
        <span className='text-ThemeTextMedium'>{question}</span>
      </CollapseButton>
      <CollapseContent className='mt-2'>
        <AILoadingPlaceholder isLoading={false} isWriting={isLoading || isWriting}>
          <div className='background-ThemeBgLight border-radius w-100 py-3 px-6'>
            {isLoading
              ? <span className='text-gradient'>Thinking...</span>
              : nl2br(response ?? '')
            }
          </div>
        </AILoadingPlaceholder>
      </CollapseContent>
    </CollapsePanel>
  );
}
